/* global axios */
// Softphone AstraCall — Central de Ligações ao vivo. Áudio = track WebRTC (Opus);
// VÍDEO = H264 sobre DataChannel via WebCodecs (setupVideoChannel), sempre aberto p/
// upgrade mid-call sem renegociar SDP. Browser-direct (CORS liberado, SSE por apiKey).
// Um RTCPeerConnection por call_id. Vídeo só em Chrome/Edge; senão áudio-only.
import { reactive, computed } from 'vue';
import { useCallsStore } from 'dashboard/stores/calls';
import { emitter } from 'shared/helpers/mitt';
import { setupVideoChannel } from './astracall/videoChannel';
import {
  videoSupported,
  VIDEO_WIDTH,
  VIDEO_HEIGHT,
  VIDEO_FPS,
} from './astracall/videoCodec';
import { openWsAudio } from './astracall/wsAudio';

const cameraConstraints = () => ({
  width: { ideal: VIDEO_WIDTH },
  height: { ideal: VIDEO_HEIGHT },
  frameRate: { ideal: VIDEO_FPS },
});

const waitIceComplete = pc =>
  new Promise(resolve => {
    if (pc.iceGatheringState === 'complete') {
      resolve();
      return;
    }
    const check = () => {
      if (pc.iceGatheringState === 'complete') {
        pc.removeEventListener('icegatheringstatechange', check);
        resolve();
      }
    };
    pc.addEventListener('icegatheringstatechange', check);
  });

// estado singleton (compartilhado por todo o painel)
const state = reactive({
  ready: false,
  enabled: false,
  baseUrl: '',
  apiKey: '',
  clientId: '',
  // transporte de áudio: 'websocket' (call server sem UDP/atrás de proxy) ou 'webrtc'.
  // Lido 1x do GET /api/config do call server (defaultTransport) e cacheado.
  transport: '',
  accountId: null,
  sessions: [], // [{ sid, name, inbox_id, inbox_name }]
  calls: {}, // por callId: { callId, sid, status, direction, peer, held, startedAt, muted }
  // callIds com ponte de áudio WebRTC ATIVA neste navegador (reativo p/ os getters).
  // Só chamadas aqui contam como "ativas" no dock — evita hidratar fantasmas do
  // call-list (chamadas presas no call server sem call-ended) como se fossem desta aba.
  bridged: {},
});

const peers = {}; // callId -> { pc, audioEl, localStream, video, remoteVideoStream, localVideoStream, setLocalVideo }
let es = null;
let initPromise = null;

// Conversas em que ESTE atendente iniciou uma chamada de SAÍDA. Marcado
// SINCRONAMENTE no clique (antes do POST), então o broadcast `incoming` que o
// backend devolve pro próprio iniciador — que pode chegar ANTES de a chamada
// entrar no dock — já encontra a conversa aqui e o widget antigo não abre o
// popup "Atender" redundante. Limpo quando a chamada encerra.
const outgoingConvs = new Set();
const marcarSaida = convId => {
  if (convId !== undefined && convId !== null) outgoingConvs.add(String(convId));
};
// É minha chamada de saída? (por conversa — à prova de corrida — ou por callId já
// reconhecido no dock como outgoing). Usado pelo AstraCallWidget p/ ignorar o
// popup de "incoming" que o backend ecoa pro próprio iniciador.
const isMinhaSaida = ({ callId, conversationId } = {}) => {
  if (conversationId !== undefined && conversationId !== null &&
      outgoingConvs.has(String(conversationId))) return true;
  if (callId === undefined || callId === null) return false;
  return Object.values(state.calls).some(
    c => String(c.callId) === String(callId) && c.direction === 'outgoing'
  );
};

// Heartbeat do marcador "grupo de saída" no Rails (ver AstracallsController#mark_group_call).
// O listener (sidekiq) ecoa a chamada de grupo que NÓS originamos como "incoming" e tocava
// o popup "Atender". O marcador tem TTL 120s; aqui renovamos a cada 60s ENQUANTO houver
// chamada de grupo ativa no dock, e paramos (deixando expirar) quando ela encerra.
let grupoMarkTimer = null;
const pingGroupMark = sid => {
  if (!sid || !state.accountBase) return;
  axios
    .post(`${state.accountBase}/kanban/astracalls/group_mark`, { sid })
    .catch(() => {});
};
const iniciarHeartbeatGrupo = sid => {
  pingGroupMark(sid); // imediato
  if (grupoMarkTimer) return;
  grupoMarkTimer = setInterval(() => {
    const sidsAtivos = [
      ...new Set(
        Object.values(state.calls)
          .filter(c => c.group)
          .map(c => c.sid)
      ),
    ];
    if (sidsAtivos.length === 0) {
      clearInterval(grupoMarkTimer);
      grupoMarkTimer = null;
      return;
    }
    sidsAtivos.forEach(pingGroupMark);
  }, 60000);
};

const CLIENT_KEY = 'astracall_client_id';
// `crypto.randomUUID` existe em todo navegador que roda o painel (contexto
// seguro). O fallback cobre http local, sem os operadores bit a bit do snippet
// antigo — aqui o id só precisa ser único por navegador, não criptográfico.
const uuid = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.floor(Math.random() * 16);
    const v = c === 'x' ? r : (r % 4) + 8;
    return v.toString(16);
  });
};

const getClientId = () => {
  let id = localStorage.getItem(CLIENT_KEY);
  if (!id) {
    id = uuid();
    localStorage.setItem(CLIENT_KEY, id);
  }
  return id;
};

// ---- REST direto no call server ----
// noClientId: as rotas de GRUPO espelham o painel do AstraCalls (widget.js), que manda
// SÓ X-API-Key. Mandar X-Client-Id amarrava a chamada de grupo ao nosso clientId e
// atrapalhava a entrada de outros participantes. No 1:1 o X-Client-Id segue (dedup de aba).
const rest = async (sid, path, { method = 'POST', body, noClientId = false } = {}) => {
  const headers = {
    'X-API-Key': state.apiKey,
    'Content-Type': 'application/json',
  };
  if (!noClientId) headers['X-Client-Id'] = state.clientId;
  const res = await fetch(`${state.baseUrl}/api/sessions/${sid}/${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
};

// ---- estado das chamadas ----
// O call server fala `inbound`/`outbound` no snapshot `call-list` e `incoming` no
// evento avulso — e o snapshot chega DEPOIS, sobrescrevendo o direction do evento.
// Sem normalizar aqui, a chamada de entrada virava `inbound`, `incomingCalls`
// ficava vazia e o CallDock nunca tocava.
const DIRECTION_ALIAS = {
  inbound: 'incoming',
  incoming: 'incoming',
  outbound: 'outgoing',
  outgoing: 'outgoing',
};
// `1449…@lid` (LID de privacidade), `…@s.whatsapp.net`, `…@g.us`: endereçamento
// interno do WhatsApp, não telefone. Não serve para mostrar nem para o histórico.
const ehEnderecoInterno = v => String(v || '').includes('@');

// Identidade resolvida pelo backend (nome/telefone do contato), por callId. Fica
// guardada à parte para sobreviver a qualquer recriação da entrada a partir de um
// retrato do servidor, que só carrega o endereço interno.
const identidadeResolvida = {};

// Sessões cuja caixa tem bot de voz ativo. A IA atende sozinha, então o card e o
// toque NÃO podem aparecer: o atendente entraria numa conversa que não é dele. O
// ticket é quem mostra que existe ligação em andamento com a IA.
const temBotDeVoz = sid =>
  !!sid && state.sessions.some(s => s.sid === sid && s.voice_bot);

// Chamadas que a IA nos entregou (transferência): essas PODEM tocar, mesmo vindo
// de uma sessão com bot — foi a própria IA que pediu para chamar este atendente.
const entreguesPelaIa = {};

// Chamadas que o BACKEND avisou que a IA vai atender (evento voice_bot). Usado
// quando a sessão da ligação NÃO casa com uma do config (ex.: ligação SIP em modo
// agente): mesmo sem `temBotDeVoz`, suprimimos o toque do SSE direto por call_id.
const ligacoesComBot = new Set();

const upsertCall = c => {
  const id = c.callId || c.id;
  if (!id) return;
  const anterior = state.calls[id] || {};
  const patch = { ...c, callId: id };
  const dir = DIRECTION_ALIAS[String(patch.direction || '').toLowerCase()];
  if (dir) patch.direction = dir;
  else delete patch.direction; // não deixa um valor desconhecido apagar o que já sabemos

  // Só o evento `incoming` traz o telefone; os retratos `call-list`/`call-status`
  // mandam apenas o endereço interno — e chegam DEPOIS. Sem esta guarda, o LID
  // sobrescrevia o número bom e o dock passava a exibir `1449…@lid`.
  const candidato = patch.phone || patch.peer;
  if (candidato) {
    // só recusa o candidato quando ele é interno E o que já temos é melhor
    const jaTemosMelhor =
      ehEnderecoInterno(candidato) &&
      anterior.peer &&
      !ehEnderecoInterno(anterior.peer);
    patch.peer = jaTemosMelhor ? anterior.peer : candidato;
  } else {
    delete patch.peer;
  }

  const resolvida = identidadeResolvida[id];
  if (resolvida) {
    if (
      resolvida.contact_name &&
      !patch.contact_name &&
      !anterior.contact_name
    ) {
      patch.contact_name = resolvida.contact_name;
    }
    if (
      resolvida.peer &&
      ehEnderecoInterno(patch.peer || anterior.peer || '')
    ) {
      patch.peer = resolvida.peer;
    }
  }

  state.calls[id] = { ...anterior, ...patch };
};
const cleanupPeer = id => {
  delete state.bridged[id];
  const p = peers[id];
  if (!p) return;
  try {
    p.wsAudio?.close();
    p.video?.close();
    p.localVideoStream?.getTracks().forEach(t => t.stop());
    p.localStream?.getTracks().forEach(t => t.stop());
    p.pc?.close();
    p.audioEl?.remove();
  } catch (e) {
    /* noop */
  }
  delete peers[id];
};

const removeCall = id => {
  // libera a marca de "saída minha" desta conversa (senão um inbound futuro na
  // MESMA conversa seria indevidamente suprimido pelo widget antigo)
  const convId = state.calls[id]?.conversation_id;
  if (convId !== undefined && convId !== null) outgoingConvs.delete(String(convId));
  cleanupPeer(id);
  delete identidadeResolvida[id];
  delete state.calls[id];
};

// Transporte de áudio ('websocket'|'webrtc'), definido no aplicarConfig a partir do
// default_transport que o BACKEND resolve (o token efêmero do navegador não acessa o
// /api/config do call server — dava 401). Default seguro: 'webrtc'.
const getTransport = () => state.transport || 'webrtc';

// Ponte de ÁUDIO por WebSocket (PCM16). Usada quando o call server pede
// defaultTransport=websocket, ou como fallback quando o ICE do WebRTC não fecha.
// Áudio-only: sem vídeo, sem pc. mute (track.enabled) e cleanup reusam localStream;
// o WS é fechado por p.wsAudio.close() no cleanupPeer.
const openBridgeWs = async (sid, callId) => {
  const localStream = await navigator.mediaDevices.getUserMedia({
    audio: true,
  });
  const wsBase = state.baseUrl.replace(/^http/i, 'ws').replace(/\/$/, '');
  const wsUrl =
    `${wsBase}/api/sessions/${sid}/calls/${callId}/ws` +
    `?apiKey=${encodeURIComponent(state.apiKey)}` +
    `&clientId=${encodeURIComponent(state.clientId)}`;
  let wsAudio;
  try {
    wsAudio = await openWsAudio({ wsUrl, micStream: localStream });
  } catch (e) {
    localStream.getTracks().forEach(t => t.stop());
    throw e;
  }
  peers[callId] = {
    pc: null,
    audioEl: null,
    localStream,
    wsAudio,
    remoteVideoStream: null,
    localVideoStream: null,
    transport: 'ws',
  };
  state.bridged[callId] = true;
};

// Ponte de ÁUDIO da CHAMADA EM GRUPO. Mesmo transporte do 1:1 por WebSocket (PCM16),
// só muda a URL (.../calls/group/ws) — o downlink é o MIX de todos os participantes.
// events=1 traz frames de texto JSON (roster/estado) que o openWsAudio ignora.
const openGroupBridge = async (sid, callId) => {
  const localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const wsBase = state.baseUrl.replace(/^http/i, 'ws').replace(/\/$/, '');
  // ESPELHA O PAINEL DO ASTRACALLS (que funciona): a URL do WS de grupo leva SÓ a
  // apiKey. Antes mandávamos `&clientId=...&events=1` — o `events=1` fazia o servidor
  // tratar este WS como CANAL DE EVENTOS, não como a perna de ÁUDIO do host; a chamada
  // não tocava pros outros, não ficava disponível no grupo e caía sozinha. O roster vem
  // pela REST (loadGroupRoster), igual ao painel.
  const wsUrl =
    `${wsBase}/api/sessions/${sid}/calls/group/ws` +
    `?apiKey=${encodeURIComponent(state.apiKey)}`;
  let wsAudio;
  try {
    wsAudio = await openWsAudio({
      wsUrl,
      micStream: localStream,
      // DIAGNÓSTICO: o servidor manda eventos JSON no WS de grupo (events=1):
      // participante entrou/saiu, estado, etc. Logamos p/ separar o que é nosso do
      // que é do AstraCalls, e atualizamos a lista de participantes ao vivo.
      onEvent: ev => {
        // eslint-disable-next-line no-console
        console.info('[AstraCall grupo] evento WS:', ev);
        aplicarEventoGrupo(callId, ev);
      },
    });
  } catch (e) {
    localStream.getTracks().forEach(t => t.stop());
    // eslint-disable-next-line no-console
    console.error('[AstraCall grupo] falha ao abrir áudio WS:', e?.message || e);
    throw e;
  }
  // eslint-disable-next-line no-console
  console.info('[AstraCall grupo] áudio WS aberto:', wsUrl.replace(/apiKey=[^&]+/, 'apiKey=***'));
  peers[callId] = {
    pc: null,
    audioEl: null,
    localStream,
    wsAudio,
    remoteVideoStream: null,
    localVideoStream: null,
    transport: 'ws',
    group: true,
  };
  state.bridged[callId] = true;
};

// Reage aos eventos do WS de grupo (roster/participante). Formato exato depende do
// AstraCalls — tratamos os campos mais prováveis e deixamos o log cru p/ o resto.
const aplicarEventoGrupo = (callId, ev) => {
  if (!ev || typeof ev !== 'object') return;
  const tipo = ev.type || ev.event;
  if (ev.roster || tipo === 'roster') {
    const roster = ev.roster || ev.data || {};
    const nomes = Object.values(roster)
      .map(p => p?.name || p?.phone)
      .filter(Boolean);
    upsertCall({ callId, roster, participants: nomes });
  }
};

// ---- abre a ponte da chamada. Áudio = track WebRTC (Opus) + vídeo via DataChannel;
// ou áudio por WebSocket (PCM16) quando a rede/servidor não fecha o WebRTC ----
const openBridge = async (sid, callId, { video = false } = {}) => {
  cleanupPeer(callId);
  if ((await getTransport()) === 'websocket') {
    await openBridgeWs(sid, callId);
    return;
  }
  const wantVideo = !!video && videoSupported();
  const localStream = await navigator.mediaDevices.getUserMedia({
    audio: true,
    video: wantVideo ? cameraConstraints() : false,
  });
  // sem STUN/TURN — o call server é ice-lite; mandamos o SDP com os candidatos já reunidos
  const pc = new RTCPeerConnection({ iceServers: [] });

  // Fallback áudio-por-WS: se o ICE NUNCA conectar (rede/servidor sem UDP), a chamada
  // ficaria MUDA; então trocamos pro transporte por WebSocket. Dispara no 'failed' ou
  // se não conectar em 7s. Só uma vez, e só enquanto esta pc for a ponte ativa.
  let iceUp = false;
  let fellBack = false;
  const fallbackToWs = reason => {
    if (fellBack || iceUp || peers[callId]?.pc !== pc) return;
    fellBack = true;
    // eslint-disable-next-line no-console
    console.warn('[AstraCall] WebRTC sem áudio, caindo pro WebSocket', {
      callId,
      reason,
    });
    cleanupPeer(callId);
    openBridgeWs(sid, callId).catch(err =>
      // eslint-disable-next-line no-console
      console.error('[AstraCall] fallback WS falhou', err?.message || err)
    );
  };
  pc.oniceconnectionstatechange = () => {
    const st = pc.iceConnectionState;
    if (st === 'connected' || st === 'completed') iceUp = true;
    else if (st === 'failed') fallbackToWs('ice-failed');
  };

  // Só a track de ÁUDIO entra no PC; o vídeo vai pelo canal WebCodecs abaixo.
  // `addTrack` já cria o transceiver em `sendrecv`, que ENVIA e RECEBE. Havia
  // aqui um `addTransceiver('audio', {direction:'recvonly'})` extra, redundante,
  // que fazia a offer sair com DUAS linhas `m=audio` (sendrecv + recvonly) —
  // entrou junto com o vídeo em 5c713e48ae e é o suspeito do áudio de entrada
  // sumir. Uma linha `sendrecv` é a offer correta de qualquer forma.
  localStream.getAudioTracks().forEach(t => pc.addTrack(t, localStream));

  const audioEl = document.createElement('audio');
  audioEl.autoplay = true;
  audioEl.dataset.astracall = callId;
  document.body.appendChild(audioEl);
  pc.ontrack = e => {
    // `e.streams` vem VAZIO quando o answer não traz `a=msid` — que é o caso de
    // servidor de SDP escrito à mão. Sem o fallback, `srcObject` virava
    // undefined e ficava mudo mesmo com o RTP chegando.
    const stream = e.streams?.[0] || new MediaStream([e.track]);
    audioEl.srcObject = stream;
    // autoplay pode ser recusado mesmo depois do clique de atender
    audioEl.play?.().catch(err => {
      // eslint-disable-next-line no-console
      console.warn('[AstraCall] audio remoto nao tocou', err?.name || err);
    });
  };

  // vídeo: DataChannel "h264" SEMPRE aberto (mesmo em chamada de áudio) p/ upgrade sem SDP
  const vchan = setupVideoChannel(pc);

  // câmera local começa ligada só se a chamada nasceu em vídeo
  let camTrack = wantVideo ? localStream.getVideoTracks()[0] || null : null;
  const p = {
    pc,
    audioEl,
    localStream,
    video: vchan,
    remoteVideoStream: vchan.remoteVideoStream,
    localVideoStream: null,
  };
  if (camTrack) {
    p.localVideoStream = new MediaStream([camTrack]);
    vchan.startSender(camTrack);
  }

  // liga/desliga a câmera no meio da chamada (upgrade/downgrade), sem renegociar SDP
  p.setLocalVideo = async on => {
    if (on) {
      if (camTrack) return true;
      if (!videoSupported()) return false;
      const cam = await navigator.mediaDevices.getUserMedia({
        video: cameraConstraints(),
      });
      camTrack = cam.getVideoTracks()[0] || null;
      if (!camTrack) return false;
      p.localVideoStream = new MediaStream([camTrack]);
      vchan.startSender(camTrack);
      return true;
    }
    vchan.stopSender();
    try {
      camTrack?.stop();
    } catch (e) {
      /* noop */
    }
    camTrack = null;
    p.localVideoStream = null;
    return false;
  };

  peers[callId] = p;
  state.bridged[callId] = true;

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  await waitIceComplete(pc);
  const { sdp_answer: answer } = await rest(sid, `calls/${callId}/webrtc`, {
    body: { sdp_offer: pc.localDescription.sdp },
  });
  await pc.setRemoteDescription({ type: 'answer', sdp: answer });

  // se em 7s o ICE não subiu, assume rede sem UDP e cai pro áudio por WebSocket
  setTimeout(() => fallbackToWs('ice-timeout'), 7000);

  // "não escuto o cliente" chegava sempre sem nenhuma pista. Se o answer não
  // negociou o sentido de ENTRADA do áudio, o problema é o SDP e não o
  // microfone/alto-falante de quem reclamou — deixa isso dito no console.
  const entrada = pc
    .getTransceivers()
    .some(
      t =>
        t.receiver?.track?.kind === 'audio' &&
        ['sendrecv', 'recvonly'].includes(t.currentDirection)
    );
  if (!entrada) {
    // eslint-disable-next-line no-console
    console.warn(
      '[AstraCall] o answer nao negociou audio de ENTRADA — a voz do cliente nao vai chegar',
      {
        callId,
        direcoes: pc.getTransceivers().map(t => t.currentDirection),
        mLinesNaOffer: (pc.localDescription?.sdp || '')
          .split('\n')
          .filter(l => l.startsWith('m=')).length,
      }
    );
  }
};

// O FloatingCallWidget (fluxo de voz nativo) também mostra esta chamada e toca o
// ringtone em loop, porque o listener grava uma Call/mensagem de voz no ticket. Quem
// conduz a ligação AstraCall é o CallDock, então assim que ela deixa de estar
// chamando — atendida por alguém, ou encerrada — o card nativo tem que sair, senão o
// som fica tocando para sempre. Cada aba recebe os mesmos eventos SSE e limpa a sua.
const silenciarCardNativo = callId => {
  if (!callId) return;
  try {
    useCallsStore().removeCall(callId);
  } catch (e) {
    /* store ainda não montada nesta aba */
  }
};

// O call server só manda `phone` quando ELE conhece o número; fora isso vem apenas
// o endereço interno (`1449…@lid`), e era isso que aparecia no card. O nosso backend
// resolve o LID para o contato de verdade (mapeamento aprendido pelas mensagens) e
// já transmite o resultado no `kanban.astracall.incoming` — usamos ele como fonte
// autoritativa da identidade de quem está ligando.
emitter.on('kanban:astracall:incoming', data => {
  if (!data?.call_id) return;
  identidadeResolvida[data.call_id] = {
    peer: data.peer,
    contact_name: data.contact_name,
  };
  // `handoff`: a IA estava na linha e decidiu passar para um humano. Aqui o evento
  // deixa de ser só identidade e passa a CRIAR o card tocando — é o único caminho
  // de toque numa caixa com bot, e chega apenas para o atendente escolhido.
  // A IA vai atender em segundo plano: silencia o toque fantasma por call_id. O
  // navegador ouve o SSE do call server DIRETO e toca quando não reconhece a sessão
  // como bot (ligação SIP). Aqui removemos o card/ring e barramos recriações pelos
  // retratos `call-list` seguintes (via ligacoesComBot em deixarPassar).
  if (data.voice_bot && !data.handoff) {
    ligacoesComBot.add(String(data.call_id));
    silenciarCardNativo(data.call_id);
    removeCall(data.call_id);
    return;
  }

  const entregue = !!data.handoff;
  if (entregue) {
    entreguesPelaIa[data.call_id] = true;
    // O FloatingCallWidget nativo toca ringtone.mp3 EM LOOP enquanto vê a chamada
    // como não atendida. Numa transferência ela já está em andamento, então ele
    // nunca sai sozinho e o som seguia tocando depois de o atendente atender.
    silenciarCardNativo(data.call_id);
  }
  upsertCall({
    callId: data.call_id,
    peer: data.peer,
    contact_name: data.contact_name,
    conversation_id: data.conversation_id,
    ...(entregue
      ? { sid: data.sid, status: 'ringing', direction: 'incoming' }
      : {}),
  });
});

// ---- SSE ----
// Uma chamada só é ignorada enquanto NINGUÉM a entregou para este atendente e ela
// ainda está tocando: depois de aceita (ou entregue pela IA) ela precisa aparecer
// normalmente no dock, senão o atendente fica sem controle da ligação que pegou.
const deixarPassar = (sid, callId, status) => {
  // o retrato `call-list` nem sempre repete a sessão; se já conhecemos a chamada,
  // usamos a sessão que guardamos dela em vez de deixar passar por falta de dado
  const sessao = sid || state.calls[callId]?.sid;
  // bot por SESSÃO (config) OU por CALL_ID (aviso do backend, caso SIP) — qualquer
  // um dos dois já segura o toque; a transferência (entreguesPelaIa) sempre passa.
  const ehBot = temBotDeVoz(sessao) || ligacoesComBot.has(String(callId));
  return (
    !ehBot ||
    entreguesPelaIa[callId] ||
    (status && status !== 'ringing')
  );
};

// callIds que já foram para o histórico. O `call-ended` do servidor chega DEPOIS de
// nós mesmos desligarmos, então sem esse controle a mesma ligação entraria duas vezes.
const jaRegistradas = new Set();

// registra a ligação encerrada no histórico (Central de Ligações).
// PRECISA ser chamado ANTES de removeCall: quem desliga é quem apaga a chamada do
// estado, e o `call-ended` que chega em seguida só encontrava `undefined` — era por
// isso que a ligação encerrada PELO ATENDENTE nunca aparecia no relatório.
const logEndedCall = (call, { status } = {}) => {
  if (!call || !call.callId) return;
  if (jaRegistradas.has(call.callId)) return;
  const session = state.sessions.find(s => s.sid === call.sid);
  // VAZAMENTO ENTRE CONTAS: o SSE do AstraCalls pode entregar o call-ended de OUTRA
  // conta a este navegador. Só gravamos ligação de uma sessão DESTA conta — senão a
  // ligação (com telefone/nome do contato) ia parar no histórico da conta errada.
  // Também garante que o inbox_id enviado é sempre o da caixa certa, nunca null.
  if (!session) return;
  jaRegistradas.add(call.callId);
  // o Set é da sessão do browser; um teto evita ele crescer sem fim numa aba aberta o dia todo
  if (jaRegistradas.size > 500) {
    jaRegistradas.delete(jaRegistradas.values().next().value);
  }
  const started = call.startedAt ? new Date(call.startedAt) : null;
  const dur = started
    ? Math.max(0, Math.round((Date.now() - started) / 1000))
    : 0;
  const answered = !!call.startedAt && call.status === 'connected';
  axios
    .post(`${state.accountBase}/kanban/call_history`, {
      conversation_id: call.conversation_id || null,
      // id da chamada no provedor: é por ele que a gravação (o arquivo tem esse
      // nome) e o registro nativo em `calls` se encontram depois
      provider_call_id: call.callId || null,
      contact_phone: call.peer || call.phone || '',
      contact_name: call.contact_name || '',
      direction: call.direction === 'incoming' ? 'INCOMING' : 'OUTGOING',
      status: status || (answered ? 'ENDED' : 'NOT_ANSWERED'),
      instance_name: session?.name || '',
      inbox_id: session?.inbox_id || null,
      duration_seconds: dur,
      call_method: 'astracall',
    })
    .catch(() => {});
};

// Status que são ANTES de conectar. A SSE do servidor não pode REBAIXAR uma chamada
// que ESTE navegador já colocou em 'connected' (ponte de áudio aberta) — senão a
// chamada de grupo, que o servidor reporta como "chamando" enquanto cada participante
// atende, voltava pra "conectando" e travava lá. Só 'call-ended' encerra.
const PRE_CONNECTED = ['ringing', 'starting', 'calling', 'connecting', 'dialing'];
const mergeServerCall = c => {
  const id = c.callId || c.id;
  const cur = state.calls[id];
  if (
    cur &&
    state.bridged[id] &&
    cur.status === 'connected' &&
    PRE_CONNECTED.includes(String(c.status))
  ) {
    const { status, ...rest } = c; // mantém o resto, ignora o status rebaixado
    upsertCall({ ...rest, callId: id });
    return;
  }
  upsertCall({ ...c, callId: id });
};

const handleEvent = msg => {
  // DIAGNÓSTICO grupo: loga eventos SSE de uma chamada de grupo conhecida.
  if (state.calls[msg.id]?.group || (msg.calls || []).some(c => state.calls[c.callId || c.id]?.group)) {
    // eslint-disable-next-line no-console
    console.info('[AstraCall grupo] SSE:', msg.type, JSON.stringify(msg).slice(0, 300));
  }
  switch (msg.type) {
    case 'call-list':
      (msg.calls || [])
        .filter(c =>
          deixarPassar(c.sid || msg.sessionId, c.callId || c.id, c.status)
        )
        .forEach(mergeServerCall);
      break;
    case 'call-status':
      // faltava o filtro aqui: um `call-status` com status "ringing" criava o card
      // do mesmo jeito, e ele aparecia por um instante até o status virar outro —
      // era esse o pisca-pisca do widget nas caixas com bot de voz
      if (!deixarPassar(msg.sessionId || msg.sid, msg.id, msg.status)) break;
      mergeServerCall({ ...msg, callId: msg.id });
      if (msg.status && msg.status !== 'ringing') silenciarCardNativo(msg.id);
      break;
    case 'incoming':
      if (!deixarPassar(msg.sessionId, msg.id, 'ringing')) break;
      upsertCall({
        callId: msg.id,
        sid: msg.sessionId,
        status: 'ringing',
        direction: 'incoming',
        peer: msg.phone || msg.peer,
        contact_name: msg.name,
        video: msg.video,
      });
      break;
    case 'incoming-claimed': {
      silenciarCardNativo(msg.id);
      // O dono vem NO evento. Lendo do estado pegávamos o valor velho (ainda null,
      // porque o snapshot com o owner só chega depois): a própria aba que atendeu
      // apagava a chamada, e o `call-list` seguinte a recriava sem o nome nem o
      // telefone — era por isso que o card virava `…@lid` só DEPOIS de atender.
      const deOutroAtendente = msg.owner && msg.owner !== state.clientId;
      if (deOutroAtendente) removeCall(msg.id);
      else upsertCall({ callId: msg.id, owner: msg.owner });
      break;
    }
    case 'call-ended':
      logEndedCall(state.calls[msg.id]);
      silenciarCardNativo(msg.id);
      removeCall(msg.id);
      ligacoesComBot.delete(String(msg.id));
      break;
    case 'video-state':
      // kind: 'upgrade-request' (peer pediu vídeo) | 'state' (peer ligou/desligou câmera)
      upsertCall({
        callId: msg.id,
        remoteVideo: !!msg.peerVideo,
        localVideo: !!msg.localVideo,
        upgradeIncoming:
          msg.kind === 'upgrade-request' && !!msg.upgradeIncoming,
      });
      break;
    default:
      break;
  }
};

const openStream = () => {
  if (es) es.close();
  // accountId explícito, senão o call server trata a conexão como ADMIN (escopo 0)
  // e manda os eventos de TODAS as contas — chamada tocando e contato criado na
  // conta errada. A rede de segurança dele deriva a conta do padrão
  // `astrachat_accN`, mas o clientId daqui é um id por NAVEGADOR: sem o parâmetro,
  // esta conexão ficava descoberta.
  // inboxId só quando a conta tem uma sessão: com várias, ele desempataria errado.
  const p = new URLSearchParams({
    accountId: String(state.accountId ?? ''),
    clientId: state.clientId,
    apiKey: state.apiKey,
  });
  if (state.sessions.length === 1 && state.sessions[0]?.inbox_id) {
    p.set('inboxId', String(state.sessions[0].inbox_id));
  }
  es = new EventSource(`${state.baseUrl}/api/events?${p.toString()}`);
  es.onmessage = ev => {
    try {
      handleEvent(JSON.parse(ev.data));
    } catch (e) {
      /* noop */
    }
  };
  es.onerror = () => {
    /* EventSource reconecta sozinho */
  };
};

// ---- init ----
// A credencial que chega aqui é um token EFÊMERO por conta (o call server emite;
// ver Kanban::AstracallWidgetToken). Ele expira, então renovamos antes disso e
// reabrimos o stream — senão o softphone fica aberto na tela e para de receber
// evento sem dar sinal. Chave fixa vem sem validade e não agenda nada.
let renovacaoTimer = null;
const MARGEM_RENOVACAO = 60 * 1000; // 1 min antes de vencer

const agendarRenovacao = expiraEm => {
  clearTimeout(renovacaoTimer);
  if (!expiraEm) return;
  const faltam = new Date(expiraEm).getTime() - Date.now() - MARGEM_RENOVACAO;
  if (Number.isNaN(faltam)) return;
  renovacaoTimer = setTimeout(renovarCredencial, Math.max(faltam, 10 * 1000));
};

const aplicarConfig = cfg => {
  state.enabled = !!cfg.enabled;
  state.baseUrl = cfg.base_url || '';
  state.apiKey = cfg.api_key || '';
  // transporte de áudio resolvido pelo backend (o token efêmero não acessa o
  // /api/config do call server — dava 401 se o front tentasse ler direto).
  state.transport =
    cfg.default_transport === 'websocket' ? 'websocket' : 'webrtc';
  state.sessions = cfg.sessions || [];
  agendarRenovacao(cfg.api_key_expires_at);
};

const renovarCredencial = async () => {
  try {
    const { data } = await axios.get(
      `${state.accountBase}/kanban/astracalls/config`
    );
    aplicarConfig(data.data || {});
    if (state.enabled) openStream(); // o token vai na URL do SSE: precisa reabrir
  } catch (e) {
    agendarRenovacao(new Date(Date.now() + 5 * 60 * 1000).toISOString()); // tenta de novo
  }
};

const ensureInit = async accountId => {
  if (state.ready) return state.enabled;
  if (initPromise) return initPromise;
  state.accountBase = `/api/v1/accounts/${accountId}`;
  state.accountId = accountId;
  initPromise = (async () => {
    try {
      const { data } = await axios.get(
        `${state.accountBase}/kanban/astracalls/config`
      );
      state.clientId = getClientId();
      aplicarConfig(data.data || {});
      if (state.enabled) openStream();
    } catch (e) {
      state.enabled = false;
    } finally {
      state.ready = true;
    }
    return state.enabled;
  })();
  return initPromise;
};

// ---- ações públicas ----
const dial = async ({ sid, phone, conversationId, contactName }) => {
  marcarSaida(conversationId); // antes do POST: fecha a corrida com o broadcast
  const { call } = await rest(sid, 'calls', { body: { phone } });
  const callId = call.callId;
  upsertCall({
    callId,
    sid,
    status: 'starting',
    direction: 'outgoing',
    peer: phone,
    conversation_id: conversationId,
    contact_name: contactName,
  });
  await openBridge(sid, callId, {});
  return callId;
};

// Inicia uma CHAMADA EM GRUPO (áudio). Resolve os membros pelo groupJid no call server.
// video é ignorado por ora (o áudio em grupo é a 1ª fase).
const dialGroup = async ({ sid, groupJid, conversationId, groupName }) => {
  marcarSaida(conversationId); // antes do POST: fecha a corrida com o broadcast
  // avisa o Rails que ESTA caixa está originando um grupo: o listener (sidekiq) ecoa a
  // chamada como "incoming" e tocava o popup "Atender" pra quem ligou. Heartbeat (renova
  // a cada 60s enquanto o grupo está ativo) porque o grupo toca os participantes por mais
  // tempo e pode re-tocar — marcador de janela única (120s) deixava escapar. Ver
  // AstracallsController#mark_group_call.
  iniciarHeartbeatGrupo(sid);
  const res = await rest(sid, 'calls/group', {
    body: { groupJid, video: false },
    noClientId: true, // espelha o painel
  });
  // eslint-disable-next-line no-console
  console.info('[AstraCall grupo] start resp (targets = nº de membros chamados):', res);
  const callId = res.callId || res.call?.callId;
  if (!callId) throw new Error(res.error || 'Falha ao iniciar chamada de grupo');
  upsertCall({
    callId,
    sid,
    status: 'starting',
    direction: 'outgoing',
    peer: groupName || 'Grupo',
    conversation_id: conversationId,
    contact_name: groupName || 'Grupo',
    group: true,
    group_jid: groupJid,
    targets: res.targets,
  });
  await openGroupBridge(sid, callId);
  // grupo não recebe os eventos call-status do 1:1 — marca ativa aqui p/ o dock
  // mostrar o timer e os controles.
  upsertCall({ callId, status: 'connected', startedAt: Date.now() });
  loadGroupRoster(sid, callId);
  return callId;
};

// roster { "<lid>": { phone, name } } p/ rotular participantes no dock
const loadGroupRoster = async (sid, callId) => {
  try {
    const res = await rest(sid, 'calls/group/roster', {
      method: 'GET',
      noClientId: true,
    });
    const roster = res.roster || res || {};
    const nomes = Object.values(roster)
      .map(p => p?.name || p?.phone)
      .filter(Boolean);
    upsertCall({ callId, roster, participants: nomes });
  } catch (e) {
    /* roster é opcional (só rótulo) */
  }
};

// Chamada que já NASCE em vídeo (`incoming` com video:true) tem que ser atendida em
// vídeo: o `openBridge` sabe subir com a câmera ligada, mas o atender sempre mandava
// `{}` e a ligação caía para só áudio — o cliente ficava vendo a própria imagem e
// nada de volta. `video` explícito permite atender só com áudio quando se quer.
// Assume uma ligação que a IA transferiu de volta ao call server. /pickup é o passo
// certo da transferência (emite call-transfer-claimed); /accept é a reserva para
// servidores que soltam a titularidade e aceitam o accept direto.
const claimEntregue = async call => {
  try {
    await rest(call.sid, `calls/${call.callId}/pickup`);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[AstraCall] /pickup da transferência falhou, tentando /accept', {
      callId: call.callId,
      erro: e?.message || e,
    });
    await rest(call.sid, `calls/${call.callId}/accept`);
  }
};

const accept = async (call, { video } = {}) => {
  const comVideo =
    (video === undefined ? !!call.video : !!video) && videoSupported();
  const entregue = !!entreguesPelaIa[call.callId];
  try {
    if (entregue) {
      // A IA entregou a ligação de volta ao call server (POST /transfer): a
      // titularidade da ponte foi SOLTA e quem ligou está em espera (música). O
      // atendente ASSUME a transferência com /pickup (emite call-transfer-claimed);
      // se o servidor não estiver em modo transferência, /accept vale como reserva
      // (já não dá 409 depois da soltura). Antes a ponte segurava a chamada e bastava
      // abrir o áudio — virou este passo quando o AstraCalls passou a soltar no /transfer.
      await claimEntregue(call);
    } else {
      await rest(call.sid, `calls/${call.callId}/accept`);
    }
    // ao conectar o áudio (/webrtc ou /ws) a chamada sai da espera sozinha
    await openBridge(call.sid, call.callId, { video: comVideo });
    if (comVideo) upsertCall({ callId: call.callId, localVideo: true });
  } catch (e) {
    // O botão do dock chamava accept() SEM catch: qualquer falha virava rejeição
    // silenciosa e o clique parecia não fazer nada. Agora o motivo aparece.
    // eslint-disable-next-line no-console
    console.error('[AstraCall] falha ao atender', {
      callId: call.callId,
      entreguePelaIa: entregue,
      erro: e?.message || e,
    });
    throw e;
  }
};
const reject = async call => {
  logEndedCall(state.calls[call.callId] || call, { status: 'REJECTED' });
  await rest(call.sid, `calls/${call.callId}/reject`).catch(() => {});
  removeCall(call.callId);
};
const hangup = async call => {
  logEndedCall(state.calls[call.callId] || call);
  // chamada de grupo encerra por endpoint próprio; 1:1 por DELETE /calls/{id}
  const path = call.group ? 'calls/group/end' : `calls/${call.callId}`;
  const method = call.group ? 'POST' : 'DELETE';
  await rest(call.sid, path, { method, noClientId: !!call.group }).catch(() => {});
  removeCall(call.callId);
};
const toggleMute = call => {
  const p = peers[call.callId];
  if (!p) return;
  const muted = !call.muted;
  p.localStream.getAudioTracks().forEach(t => {
    t.enabled = !muted;
  });
  // além do mute local, avisa o call server p/ o mic-off aparecer pro outro lado
  // (1:1). Best-effort — se o provedor não suportar p/ a chamada, ignora.
  rest(call.sid, `calls/${call.callId}/mute`, { body: { muted } }).catch(
    () => {}
  );
  upsertCall({ callId: call.callId, muted });
};

// mão levantada (sinaliza no WhatsApp). Best-effort.
const toggleHand = call => {
  const raised = !call.handRaised;
  rest(call.sid, `calls/${call.callId}/hand`, { body: { raised } }).catch(
    () => {}
  );
  upsertCall({ callId: call.callId, handRaised: raised });
};

const videoAction = (call, action) =>
  rest(call.sid, `calls/${call.callId}/video/${action}`).catch(() => {});

// liga/desliga a NOSSA câmera. Ao ligar, pede upgrade ao WhatsApp; ao desligar, downgrade.
const toggleLocalVideo = async call => {
  const p = peers[call.callId];
  if (!p || !p.setLocalVideo) return false;
  const on = !call.localVideo;
  const ok = await p.setLocalVideo(on);
  await videoAction(call, ok ? 'request' : 'stop');
  upsertCall({ callId: call.callId, localVideo: ok, upgradeIncoming: false });
  return ok;
};

// responde a um pedido de vídeo recebido do peer (SSE video-state upgrade-request)
const acceptVideo = async call => {
  const p = peers[call.callId];
  if (!p || !p.setLocalVideo) return;
  await p.setLocalVideo(true);
  await videoAction(call, 'accept');
  upsertCall({ callId: call.callId, localVideo: true, upgradeIncoming: false });
};
const rejectVideo = async call => {
  await videoAction(call, 'reject');
  upsertCall({ callId: call.callId, upgradeIncoming: false });
};

// streams para o CallDock ligar nos <video> (fora do reactive — MediaStream não vira proxy)
const remoteVideoStreamFor = callId => peers[callId]?.remoteVideoStream || null;
const localVideoStreamFor = callId => peers[callId]?.localVideoStream || null;

// ---- getters ----
const callList = computed(() => Object.values(state.calls));
// Só é "ativa" a chamada cuja ponte de áudio foi aberta NESTA aba (dial/accept).
// Assim, chamadas presas no call server que chegam via call-list (fantasmas, sem
// call-ended) não reaparecem travadas no dock a cada reload nem vazam de outro atendente.
const activeCalls = computed(() =>
  callList.value.filter(
    c =>
      (c.direction !== 'incoming' || c.status !== 'ringing') &&
      !!state.bridged[c.callId]
  )
);
const incomingCalls = computed(() =>
  callList.value.filter(
    c => c.direction === 'incoming' && c.status === 'ringing'
  )
);

export function useAstraCalls() {
  return {
    state,
    ensureInit,
    dial,
    dialGroup,
    accept,
    reject,
    hangup,
    toggleMute,
    toggleHand,
    toggleLocalVideo,
    acceptVideo,
    rejectVideo,
    remoteVideoStreamFor,
    localVideoStreamFor,
    videoSupported: videoSupported(),
    callList,
    activeCalls,
    incomingCalls,
    isMinhaSaida,
    sessionForInbox: inboxId =>
      state.sessions.find(s => String(s.inbox_id) === String(inboxId)),
  };
}
